import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { FormControl } from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { EMPTY, Subject, catchError, debounceTime, distinctUntilChanged, filter, takeUntil, tap } from "rxjs";
import { UserService, UserTeam } from "@teammood/shared/util/user";
import { TimelineItemDialogService } from "@teammood/timeline";
import { PollTemplate, PollUnderCreation, TeamPollTarget } from "./create-poll/create-poll.type";
import { CreatePollService } from "./create-poll/create-poll.service";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { PollList, PollListItem, PollOccurrence } from "./user-polls/user-polls.type";
import { PollSetupService } from "./polls-setup.service";
import { UserPollsService } from "./user-polls/user-polls.service";
import { LoggerService } from "@teammood/shared/util/logger";
import { PostNowAction } from "./poll-templates/poll-templates.type";
import { ActivatedRoute } from "@angular/router";

@Component({
    selector: "polls-setup",
    templateUrl: "./polls-setup.component.html",
    styleUrls: ['./polls-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PollsSetupComponent implements OnInit, OnDestroy{

    protected teams: UserTeam[] = [];
    protected userName: string;
    protected disableYourPollsTab: boolean = false;
    protected selectedTabIndex: number;
    protected polls: PollListItem[];
    protected filteredPolls: PollList;
    protected search_terms: string = '';
    protected searchCtrl = new FormControl<string>('');
    constructor(
        private userService: UserService,
        private timelineItemDialogService: TimelineItemDialogService,
        private createPollService: CreatePollService,
        private snackBarService: SnackBarService,
        private translate: TranslateService,
        private pollSetupService: PollSetupService,
        private userPollsService: UserPollsService,
        private logger: LoggerService,
        private route: ActivatedRoute,
    ){}

    private _destroy$ = new Subject<void>();

    async ngOnInit() {
        const user = await this.userService.get();
        if (user) {
            this.userName = user.fullname;
            this.teams = [...user.teams];
        }

        this.route.queryParams.pipe(
            filter(params => params['focus']),
            tap(() => {
                this.goToTemplateBank();
            }),
            takeUntil(this._destroy$)
        ).subscribe();

        this.loadUserPolls();
        
        this.searchCtrl.valueChanges.pipe(
            debounceTime(400),
            distinctUntilChanged(),
            tap(() => {
                this.search_terms = this.searchCtrl.getRawValue();
                this.apply_search();
            }),
        ).subscribe();
    }

    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }

    clear_search() {
        this.searchCtrl.setValue('');
        this.search_terms = '';
    }

    protected async createPoll(template: PollTemplate | null){
        const pollTemplates = []; // from scratch
        if(template){  
            pollTemplates.push(template);
        }
        const pollDialogRef = await this.timelineItemDialogService.createPoll(this.teams, pollTemplates, this.userName);

        pollDialogRef.beforeClosed().pipe(
            filter((poll) => !!poll),
            tap(async poll => {
                await this.addPoll(poll);
            }),
            takeUntil(pollDialogRef.afterClosed())
        ).subscribe();
    }

    private async addPoll(poll: PollUnderCreation) {
        await this.createPollService.createPoll(poll);
        this.loadUserPolls();
        if(poll._serializePoll){
            this.createPoll(null);
        }else{
            this.selectedTabIndex = 0;  
        }
        this.snackBarService.success(this.translate.instant('question_created'));
    } 

    protected deletePoll(occurrence: PollOccurrence){
        return this.remove(occurrence);
    }

    protected async remove(occurrence: PollOccurrence){
        const dialogRef = await this.userPollsService.openDeletionConfirmationDialog();

        dialogRef.beforeClosed().pipe(
            filter(shouldDelete => !!shouldDelete),
            tap(() => {
                return this.pollSetupService.deletePoll(occurrence.pollOccurrenceId).pipe(
                    tap(() => {
                        this.loadUserPolls();
                    }),
                    takeUntil(this._destroy$)
                ).subscribe();
            }),
            catchError(error => {
                this.logger.error('Error deleting poll:', error);
                this.snackBarService.error('Oops')
                return EMPTY;
            }),
            takeUntil(this._destroy$)
        ).subscribe();
    }


    goToTemplateBank(){
        this.selectedTabIndex = 1;

    }
    onTabChanged(event){
        this.selectedTabIndex = event.index;
    }

    apply_search() {
        this.filteredPolls.questions = this.polls;
        if(this.search_terms !== '') {
            this.filteredPolls = this.filteredPolls.search(this.search_terms);
        }
    }

    refreshPolls(event: boolean){
        if(event){
            this.loadUserPolls();
        }
    }

    protected loadUserPolls(){
        return this.pollSetupService.all().pipe(
            tap((pollList: PollList) => {
                this.polls = pollList.questions;
                this.filteredPolls = new PollList(this.polls);
                if(this.polls.length == 0){
                    this.goToTemplateBank();
                    this.disableYourPollsTab = true;
                }else{
                    this.disableYourPollsTab = false;
                }
            }),
            takeUntil(this._destroy$)
        ).subscribe()
    }

    protected async openPostNowDialog(template: PollTemplate){
        const pollTemplates = [template];
        const pollDialogRef = await this.timelineItemDialogService.postPollNow(this.teams, pollTemplates, this.userName);

        pollDialogRef.beforeClosed().pipe(
            tap(async (action: PostNowAction) => {
                if(action === 'post'){
                    const pollToPost = PollUnderCreation.fromTemplate(template);
                    const teamIds = this.teams.map(team => team.id);
                    pollToPost.target = { teamIds: teamIds } as TeamPollTarget; 
                    await this.addPoll(pollToPost);
                }
                if(action === 'customize'){
                    this.createPoll(template);
                }
            }),
            takeUntil(pollDialogRef.afterClosed())
        ).subscribe();
    }
}